import { raceFrame } from './race-frame';

const DEFAULT_TIMEOUT_MS = 5000;

export type WaitForElementDimensionsParams = {
	element: HTMLElement;
	timeoutMs?: number;
};

/**
 * Resolves once the element reports non-zero layout dimensions, re-reading one
 * frame at a time (getBoundingClientRect forces the reflow, so a backgrounded
 * export still lays out). Rejects after `timeoutMs` so a page whose content
 * never gains bounds fails loudly instead of stalling html2canvas, which throws
 * 'Unable to find element in cloned iframe' or hangs on a zero-sized element.
 * @param params the element to measure and an optional timeout
 */
export const waitForElementDimensions = (params: WaitForElementDimensionsParams): Promise<void> => {
	const { element, timeoutMs = DEFAULT_TIMEOUT_MS } = params;

	return new Promise<void>((resolve, reject) => {
		const deadline = Date.now() + timeoutMs;

		const check = (): void => {
			const { width, height } = element.getBoundingClientRect();
			if (width > 0 && height > 0) {
				resolve();
				return;
			}

			if (Date.now() >= deadline) {
				reject(new Error('waitForElementDimensions: element still has zero dimensions after timeout'));
				return;
			}

			raceFrame(check);
		};

		check();
	});
};
